import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { BusinessService, SettingsRow } from '../../../types/businesses';
import { getBusinessShopUrl } from '../../../helpers/businesses';
import { LoadedUser } from '../../../interfaces';
import SettingsSection from '../SettingsSection';
import useGetBusinessServices from '../../../hooks/businesses/useGetBusinessServices';
import useUser from '../../../hooks/global/useUser';
import useGetWaivers from '../../../hooks/waivers/useGetWaivers';
import SettingsGridRow, { SettingsGridRowCell } from '../SettingsGridRow';
import useConfirm from '../../../hooks/useConfirm';
import useFeatureGuard from '../../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../../types/subscriptions';
import useArchiveService from '../../../hooks/businesses/useArchiveService';
import ServiceFormModal from './ServiceFormModal';

function ServiceRow({
  service,
  services,
}: {
  service: BusinessService;
  services: BusinessService[];
}) {
  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);
  const showWaiversFeatures =
    hasWaiversFeature && !!services?.some(s => !!s.waiver);

  const waiverName = hasWaiversFeature
    ? service?.waiver?.name || 'No waiver'
    : null;

  return (
    <SettingsGridRow
      cells={
        [
          {
            id: 'name',
            text: service?.serviceName || 'Unnamed service',
          },
          {
            id: 'employees',
            text: `${
              service?.employeesToServices?.length || 0
            } service providers`,
          },
          showWaiversFeatures && {
            id: 'waiver',
            text: waiverName,
          },
        ].filter(Boolean) as SettingsGridRowCell[]
      }
    />
  );
}

function ServicesSettings() {
  const confirm = useConfirm();
  const { user, reloadUser } = useUser<LoadedUser>();
  const {
    services = [],
    isLoading,
    isEnabled,
    refetch,
  } = useGetBusinessServices();
  const { refetch: refetchWaivers } = useGetWaivers();
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [editingService, setEditingService] =
    React.useState<BusinessService | null>(null);

  const {
    mutate: archiveService,
    isPending: isArchivingService,
    variables,
  } = useArchiveService({
    onSuccess: () => {
      toast.success('Service archived successfully.');
      reloadUser();
      refetch();
    },
    onError: () => {
      toast.error('Failed to archive service.');
    },
  });

  if (!isEnabled) return null;

  const rows: SettingsRow[] = isLoading
    ? [
        {
          id: 'loading',
          component: (
            <Box
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center">
              <CircularProgress color="primary" />
            </Box>
          ),
        },
      ]
    : !services?.length
    ? [
        {
          id: 'empty',
          component: (
            <Typography variant="body2">
              Add any non-tattoo services that you offer here, like Piercing,
              PMU, Tooth Gems, etc.
            </Typography>
          ),
        },
      ]
    : services.map(service => ({
        id: service.serviceId.toString(),
        component: (
          <ServiceRow
            key={service.serviceId}
            service={service}
            services={services}
          />
        ),
        dropdown: {
          options: [
            {
              label: 'Edit',
              action: () => {
                setEditingService(service);
                setIsFormOpen(true);
              },
            },
            {
              label: 'Archive',
              action: async () => {
                const confirmed = await confirm({
                  title: 'Archive Service',
                  message:
                    'Are you sure you want to archive this service? This action cannot be undone.',
                  positiveText: 'Archive',
                  negativeText: 'Cancel',
                });
                if (!confirmed) return;

                archiveService(service.serviceId);
              },
            },
          ],
          isLoading: isArchivingService && variables === service.serviceId,
        },
      }));

  return (
    <>
      <SettingsSection
        title="Other Services"
        subtitle="Add any non-tattoo services that you offer here, like Piercing, PMU, Tooth Gems, etc."
        rows={rows}
        action={{
          type: 'button',
          text: 'Add New Service',
          onClick: () => {
            setEditingService(null);
            setIsFormOpen(true);
          },
          disabled: false,
        }}
        copy={
          services?.length
            ? {
                content: getBusinessShopUrl(user?.business),
              }
            : undefined
        }
      />
      <ServiceFormModal
        open={isFormOpen}
        service={editingService}
        onClose={() => setIsFormOpen(false)}
        refetch={refetch}
        refetchWaivers={refetchWaivers}
      />
    </>
  );
}

export default ServicesSettings;
